// Lib
import React, { useCallback, useRef } from 'react';

// Types
import { ImageModalV2Mode } from './useImageModalV2Mode';

const MODAL_INTERACTIVE_CONTENT_SELECTOR =
    '.ImageModalV2Content-image-wrapper, .ImageModalV2-edit-overlay, .ImageModalV2-drawing-overlay, .ImageModalV2PrimaryTools, .ImageModalV2SecondaryTools, .ImageModalToolbarSecondaryTools, .ImageEditorModalToolbar, .drawing-editor-toolbar-container, .FloatingPanel, .RomaPopover';

/**
 * Determines whether a modal click happened on image content or a toolbar control.
 */
const isModalInteractiveContentTarget = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest(MODAL_INTERACTIVE_CONTENT_SELECTOR);

const isEditMode = (mode: ImageModalV2Mode) =>
    mode === ImageModalV2Mode.CROP || mode === ImageModalV2Mode.BACKGROUND || mode === ImageModalV2Mode.DRAW;

type UseImageModalV2BackgroundCloseProps = {
    close: (event?: React.MouseEvent<HTMLDivElement> | globalThis.MouseEvent) => void;
    mode: ImageModalV2Mode;
    finishEditingMode: () => void;
    closeCallbackRef: React.MutableRefObject<((event: globalThis.MouseEvent) => void) | null>;
};

/**
 * Matches V1's mousedown/click pairing so drags that start inside modal content
 * don't close on release. Clicking the background saves drawing (when applicable) before exiting edit mode.
 */
export const useImageModalV2BackgroundClose = ({
    close,
    mode,
    finishEditingMode,
    closeCallbackRef,
}: UseImageModalV2BackgroundCloseProps) => {
    const wasMouseDownOnClosingTargetRef = useRef(false);

    const handleModalMouseDown = useCallback((event: React.MouseEvent) => {
        wasMouseDownOnClosingTargetRef.current = !isModalInteractiveContentTarget(event.target);
    }, []);

    const handleModalClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!wasMouseDownOnClosingTargetRef.current) return;
            wasMouseDownOnClosingTargetRef.current = false;

            if (!isEditMode(mode)) return close(event);

            if (mode === ImageModalV2Mode.DRAW) {
                closeCallbackRef.current?.(event.nativeEvent);
                closeCallbackRef.current = null;
            }

            finishEditingMode();
        },
        [close, closeCallbackRef, finishEditingMode, mode],
    );

    return { handleModalMouseDown, handleModalClick };
};
